import { ActivatedRoute } from '@angular/router'
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core'
import * as ProjectorActions from 'libs/gb-dashboard/projector/data-access/src/lib/+state/projectors.actions'
import { CommonModule } from '@angular/common'
import { BehaviorSubject, combineLatest, map, Observable, withLatestFrom } from 'rxjs'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { ProjectorContainersFacade } from 'libs/gb-dashboard/presenter/data-access-projector-containers/src/lib/+state/projector-containers.facade'
import { gbCheckLoaded } from '@galabid/shared/utils/operators'
import { TranslateModule } from '@ngx-translate/core'
import { IconLibrary } from '@galabid/gb-dashboard/shared/ui'
import { GbDashboardSharedUiModule } from 'libs/gb-dashboard/shared/ui/src/lib/gb-dashboard-shared-ui.module'
import { GbPresenterCanvasComponent } from '../gb-presenter-canvas/gb-presenter-canvas.component'
import { ProjectorsFacade } from 'libs/gb-dashboard/projector/data-access/src/lib/+state/projectors.facade'
import { Actions, ofType } from '@ngrx/effects'
import * as PresenterWidgetsActions from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.actions'
import { MatButtonModule } from '@angular/material/button'

interface FullscreenDocument extends Document {
  webkitFullscreenElement?: Element
  mozFullScreenElement?: Element
  msFullscreenElement?: Element
  webkitExitFullscreen?: () => Promise<void>
  mozCancelFullScreen?: () => Promise<void>
  msExitFullscreen?: () => Promise<void>
}

interface FullscreenHTMLElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void>
  msRequestFullscreen?: () => Promise<void>
}

@UntilDestroy()
@Component({
  selector: 'gb-presenter-view',
  imports: [CommonModule, TranslateModule, GbPresenterCanvasComponent, GbDashboardSharedUiModule, MatButtonModule],
  templateUrl: './gb-presenter-view.component.html',
  styleUrl: './gb-presenter-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterViewComponent implements OnInit, OnDestroy {
  @ViewChild('stageOuterContainer', { static: true }) stageOuterContainer!: ElementRef<HTMLElement>

  @ViewChild(GbPresenterCanvasComponent, { static: true }) canvasComponent!: GbPresenterCanvasComponent

  @Input() scale = 1
  @Input() previewMode = false
  thumbnailPreview = false

  IconLibrary = IconLibrary

  projectorContainerDataIsReady$ = this.projectorContainersFacade.projectorContainerDataIsReady$

  backgroundColor$ = this.projectorsFacade.selectedProjectors$.pipe(
    map((projector) => projector?.background_color || '#ffffff'),
  )

  backgroundImageUrl$ = this.projectorsFacade.selectedProjectors$.pipe(
    map((projector) => projector?.background_image?.image || null),
  )

  backgroundImageCover$ = this.projectorsFacade.selectedProjectors$.pipe(
    map((projector) => projector?.background_image_cover ?? true),
  )

  customCss$ = this.projectorsFacade.selectedProjectors$.pipe(map((projector) => projector?.custom_css))

  private isFullscreenSubject = new BehaviorSubject<boolean>(false)
  isFullscreen$ = this.isFullscreenSubject.asObservable()

  navVisible = signal(true)
  private navVisibleTimeout?: ReturnType<typeof setTimeout>

  private lastRealtimeDrawKey: string | null = null
  private lastRealtimeDrawAt = 0
  private readonly REALTIME_DRAW_DEDUPE_WINDOW_MS = 750

  private readonly fullscreenChangeHandler = () => {
    this.isFullscreenSubject.next(this.getIsFullscreen())
  }

  private readonly keydownHandler = (event: KeyboardEvent) => {
    if (this.previewMode) return
    if (event.key === 'ArrowLeft') {
      this.selectPreviousSlide()
    } else if (event.key === 'ArrowRight') {
      this.selectNextSlide()
    }
  }

  slides$ = this.projectorsFacade.allProjectors$.pipe(
    gbCheckLoaded(this.projectorContainerDataIsReady$),
    withLatestFrom(this.projectorContainersFacade.selectedProjectorContainer$),
    map(([projectors, selectedProjectorContainer]) => {
      return projectors.filter((projector) => projector.container === selectedProjectorContainer?.uuid)
    }),
  )

  currentSlideIndex$: Observable<number> = combineLatest([
    this.slides$,
    this.projectorsFacade.selectedProjectorId$,
  ]).pipe(map(([slides, selectedProjectorId]) => slides.map((slide) => slide.uuid).indexOf(selectedProjectorId || '')))

  currentSlideDisplayIndex$ = this.currentSlideIndex$.pipe(map((index) => (index >= 0 ? index + 1 : 1)))

  canGoPreviousSlide$ = combineLatest([this.currentSlideIndex$, this.slides$]).pipe(
    map(([index, slides]) => slides.length > 0 && index > 0),
  )

  canGoNextSlide$ = combineLatest([this.currentSlideIndex$, this.slides$]).pipe(
    map(([index, slides]) => slides.length > 0 && index >= 0 && index < slides.length - 1),
  )

  constructor(
    private projectorsFacade: ProjectorsFacade,
    private projectorContainersFacade: ProjectorContainersFacade,
    private actions$: Actions,
    private activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    if (this.activatedRoute.snapshot.queryParams['thumbnailpreview'] === 'true') {
      this.thumbnailPreview = true
    }
    this.fullscreenChangeHandler()
    document.addEventListener('fullscreenchange', this.fullscreenChangeHandler)
    document.addEventListener('webkitfullscreenchange', this.fullscreenChangeHandler as EventListener)
    document.addEventListener('mozfullscreenchange', this.fullscreenChangeHandler as EventListener)
    document.addEventListener('MSFullscreenChange', this.fullscreenChangeHandler as EventListener)
    document.addEventListener('keydown', this.keydownHandler)

    this.navVisibleTimeout = setTimeout(() => this.navVisible.set(false), 5000)

    this.actions$
      .pipe(ofType(PresenterWidgetsActions.triggerRaffleDrawFromRealtime), untilDestroyed(this))
      .subscribe((action) => {
        const key = `${action.data.raffle_item}:${action.data.raffle_ticket}:${action.data.prize}`
        const now = Date.now()

        if (this.lastRealtimeDrawKey === key && now - this.lastRealtimeDrawAt < this.REALTIME_DRAW_DEDUPE_WINDOW_MS) {
          return
        }

        this.lastRealtimeDrawKey = key
        this.lastRealtimeDrawAt = now

        this.canvasComponent?.triggerRaffleDrawFromRealtime(
          action.data.raffle_item,
          action.data.ticket_number,
          action.data.prize,
        )
      })

    this.actions$
      .pipe(ofType(PresenterWidgetsActions.showProjectorNotification), untilDestroyed(this))
      .subscribe((action) => {
        this.canvasComponent?.triggerNotification(action.data)
      })
  }

  ngOnDestroy(): void {
    document.removeEventListener('fullscreenchange', this.fullscreenChangeHandler)
    document.removeEventListener('webkitfullscreenchange', this.fullscreenChangeHandler as EventListener)
    document.removeEventListener('mozfullscreenchange', this.fullscreenChangeHandler as EventListener)
    document.removeEventListener('MSFullscreenChange', this.fullscreenChangeHandler as EventListener)
    document.removeEventListener('keydown', this.keydownHandler)
    clearTimeout(this.navVisibleTimeout)
  }

  toggleFullscreen(): void {
    if (this.getIsFullscreen()) {
      this.exitFullscreen()
      return
    }

    this.goFullscreen()
  }

  goFullscreen(): void {
    const fullscreenTarget = this.stageOuterContainer?.nativeElement as FullscreenHTMLElement

    if (!fullscreenTarget) return

    if (fullscreenTarget.requestFullscreen) {
      fullscreenTarget.requestFullscreen()
    } else if (fullscreenTarget.webkitRequestFullscreen) {
      fullscreenTarget.webkitRequestFullscreen()
    } else if (fullscreenTarget.msRequestFullscreen) {
      fullscreenTarget.msRequestFullscreen()
    }
  }

  private getIsFullscreen(): boolean {
    const fsDoc = document as FullscreenDocument
    return !!(
      fsDoc.fullscreenElement ||
      fsDoc.webkitFullscreenElement ||
      fsDoc.mozFullScreenElement ||
      fsDoc.msFullscreenElement
    )
  }

  exitFullscreen(): void {
    const fsDoc = document as FullscreenDocument
    if (fsDoc.exitFullscreen) {
      fsDoc.exitFullscreen()
    } else if (fsDoc.webkitExitFullscreen) {
      fsDoc.webkitExitFullscreen()
    } else if (fsDoc.mozCancelFullScreen) {
      fsDoc.mozCancelFullScreen()
    } else if (fsDoc.msExitFullscreen) {
      fsDoc.msExitFullscreen()
    }
  }

  onGeneralClick() {
    if (!this.previewMode) this.selectNextSlide()
  }

  selectNextSlide() {
    this.projectorsFacade.dispatch(ProjectorActions.selectNextSlide())
  }

  selectPreviousSlide() {
    this.projectorsFacade.dispatch(ProjectorActions.selectPreviousSlide())
  }

  selectFirstSlide() {
    this.projectorsFacade.dispatch(ProjectorActions.selectFirstSlide())
  }

  selectSlide(uuid: string) {
    this.projectorsFacade.dispatch(
      ProjectorActions.selectProjector({
        uuid,
      }),
    )
  }
}
